import { createSlice } from '@reduxjs/toolkit';
import {
    delegateQuest,
    getAllRepositories,
    getAllSkills,
    getDescriptionAnalysis,
    selectRepoId,
    SLICE_NAME,
    queryShortLongModel,
    generateTaskContext,
    calculateTaskTimeEstimation
} from './service';
import {
    Repository,
    TaskAnalysisRequestParams,
    TaskTemplate
} from 'src/types/delegate';

interface initialTypes {
    lastAnalysisRequest: TaskAnalysisRequestParams | null;
    lastAnalysisResponse: string[] | null;
    repositories: Repository[] | null;
    availableSkills: string[];
    templates: TaskTemplate[];
    isLoading: boolean;
    loadingDescription: string | null;
    error: string | null;
    selectedRepoId: number | null;
    taskTimeEstimation: number | null;
}

const initialState: initialTypes = {
    lastAnalysisRequest: null,
    lastAnalysisResponse: null,
    templates: [],
    repositories: null,
    availableSkills: [],
    isLoading: false,
    loadingDescription: null,
    error: null,
    selectedRepoId: null,
    taskTimeEstimation: null
};

export const ProjectsSlice = createSlice({
    name: SLICE_NAME,
    initialState,
    reducers: {},

    extraReducers: {
        [getDescriptionAnalysis.pending.type]: (state) => {
            state.isLoading = true;
            state.loadingDescription = 'Working on a feedback';
        },
        [getDescriptionAnalysis.fulfilled.type]: (state, { payload }) => {
            state.isLoading = false;
            state.loadingDescription = null;
            state.lastAnalysisResponse = payload.output?.feedback;
        },
        [getDescriptionAnalysis.rejected.type]: (state, action) => {
            state.isLoading = false;
            state.loadingDescription = null;
            state.error = action.payload;
        },
        [getAllRepositories.pending.type]: (state) => {
            state.isLoading = true;
            state.loadingDescription = 'Loading repositories';
        },
        [getAllRepositories.fulfilled.type]: (state, { payload }) => {
            state.isLoading = false;
            state.loadingDescription = null;
            state.repositories = payload.repositories;
        },
        [getAllRepositories.rejected.type]: (state, action) => {
            state.isLoading = false;
            state.loadingDescription = null;
            state.error = action.payload;
        },
        [getAllSkills.pending.type]: (state) => {
            state.isLoading = true;
            state.loadingDescription = 'Loading Skills';
        },
        [getAllSkills.fulfilled.type]: (state, { payload }) => {
            state.isLoading = false;
            state.loadingDescription = null;
            state.availableSkills = payload.availableSkills;
        },
        [getAllSkills.rejected.type]: (state, action) => {
            state.isLoading = false;
            state.loadingDescription = null;
            state.error = action.payload;
        },
        [selectRepoId.pending.type]: (state) => {
            state.isLoading = true;
        },
        [selectRepoId.fulfilled.type]: (state, { payload }) => {
            state.isLoading = false;
            state.selectedRepoId = payload;
        },
        [selectRepoId.rejected.type]: (state, action) => {
            state.isLoading = false;
            state.error = action.payload;
        },
        [queryShortLongModel.pending.type]: (state) => {
            state.isLoading = true;
            state.loadingDescription = 'Coming up with an initial description';
        },
        [queryShortLongModel.fulfilled.type]: (state) => {
            state.isLoading = false;
            state.loadingDescription = null;
        },
        [queryShortLongModel.rejected.type]: (state, action) => {
            state.isLoading = false;
            state.loadingDescription = null;
            state.error = action.payload;
        },
        [generateTaskContext.pending.type]: (state) => {
            state.loadingDescription = 'Generating the task context items';
            state.isLoading = true;
        },
        [generateTaskContext.fulfilled.type]: (state) => {
            state.isLoading = false;
            state.loadingDescription = null;
        },
        [generateTaskContext.rejected.type]: (state) => {
            state.isLoading = false;
            state.loadingDescription = null;
        },
        [calculateTaskTimeEstimation.pending.type]: (state) => {
            state.isLoading = true;
            state.loadingDescription =
                'Estimating work time on this task description and skills';
        },
        [calculateTaskTimeEstimation.fulfilled.type]: (state, { payload }) => {
            state.isLoading = false;
            state.taskTimeEstimation = payload;
            state.loadingDescription = null;
        },
        [calculateTaskTimeEstimation.rejected.type]: (state) => {
            state.isLoading = false;
            state.loadingDescription = null;
        },
        [delegateQuest.pending.type]: (state) => {
            state.isLoading = true;
            state.loadingDescription = 'Delegating the quest';
        },
        [delegateQuest.fulfilled.type]: (state) => {
            state.isLoading = false;
            state.loadingDescription = null;
        },
        [delegateQuest.rejected.type]: (state, action) => {
            state.isLoading = false;
            state.loadingDescription = null;
            state.error = action.payload;
        }
    }
});

export const {} = ProjectsSlice.actions;

export default ProjectsSlice.reducer;
